import cx from "classnames";
import { getHoverTipContent } from "@/Utils/Misc";
import { Switch, Text, Flex, Icon, Button, ToolIcon, Badge } from "@theorgchartdev/storybook";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import { GetPopoverOverlayProps } from "@/Utils/ComponentLib";
import { useAutoSyncHandler } from "@/hooks/useAutoSyncHandler";
import { useBlockerContext } from "@/providers/BlockerProvider";
import { appstr } from "@coreapp/utils/appstr";
import { gOrgchartApiSyncOverrideMessage } from "@coreapp/whitelabel/brand";

const SyncChartPopover = ({ doc }) => {
  const iWiz = doc?.GetIWizData();
  const importSource = iWiz?.GetImport()?.source;
  const showSyncOverrideMessage = gOrgchartApiSyncOverrideMessage && importSource === "ocAPI";

  const { shouldBlock } = useBlockerContext();

  const {
    openCtrlRef,
    disableManualRefresh,
    lastRefreshed,
    autoSyncEnabled,
    canGoLive,
    disableLiveToggle,
    refreshHoverTip,
    toggleLiveChart,
    handleRefreshClick,
    isPlanSyncDisabled,
    planSyncDisabledTooltip,
    isCustomBackgroundDirty,
    backgroundDirtyTooltip
  } = useAutoSyncHandler({ doc });

  if (!iWiz) return;

  let baseClass = "refresh-toggle-option";
  let checkedClass = autoSyncEnabled && `${baseClass}-label--checked`;

  const overlayProps = {
    title: appstr("Refresh from source"),
    otherProps: { padding: "8px 12px" },
    sizeProps: {
      maxWidth: 350,
      minWidth: 200
    }
  };

  const reference = (
    <Badge
      showBadge={false}
      color={autoSyncEnabled ? "success" : "warning"}
      overlap="square"
      pin="top-left"
      size="small"
    >
      <ToolIcon
        dataTestId={"header-refresh-trigger"}
        name={"arrows-rotate"}
        size={"medium-small"}
        disabled={isPlanSyncDisabled}
        tooltipProps={
          isPlanSyncDisabled
            ? { message: planSyncDisabledTooltip }
            : {
                ...getHoverTipContent(refreshHoverTip, null, lastRefreshed),
                referenceOpenCtrlRef: openCtrlRef
              }
        }
        aria-label={"Refresh chart from source"}
        shape={"round"}
      />
    </Badge>
  );

  const handleManualRefresh = (ignoreBlocker = false) => {
    openCtrlRef.current?.setOpen(false);

    if (!ignoreBlocker) {
      const onProceed = () => handleManualRefresh(true);
      if (shouldBlock("refreshDocument", onProceed)) {
        return;
      }
    }

    handleRefreshClick();
  };

  return (
    <ToolbarPopover
      disabled={isPlanSyncDisabled}
      otherPopoverProps={{
        offsetOptions: -2,
        matchReferenceWidth: false,
        openCtrlRef: openCtrlRef,
        reference: reference
      }}
      overlayProps={GetPopoverOverlayProps(overlayProps)}
    >
      <Flex direction={"column"} gap={"8px"}>
        {showSyncOverrideMessage ? (
          <Text size={"small"}>{appstr(gOrgchartApiSyncOverrideMessage)}</Text>
        ) : (
          <>
            <Flex alignItems={"center"} gap={"4px"}>
              <Icon
                name={autoSyncEnabled ? "arrows-rotate" : ""}
                modifier={autoSyncEnabled ? "success" : "theme"}
                size={"small"}
              />
              <Text
                size={"small"}
                modifier={"medium"}
                colorModifier={autoSyncEnabled ? "success" : "warning"}
              >
                {autoSyncEnabled
                  ? appstr("Data sync automatic")
                  : appstr("Data sync not automatic")}
              </Text>
            </Flex>
            {canGoLive && (
              <span className={baseClass}>
                <span className={`${baseClass}-switch`}>
                  <Switch
                    id={"toggleLiveChart"}
                    data-testid={"chart-mode-toolbar-sync-toggle"}
                    tooltipProps={getHoverTipContent("Auto-sync")}
                    onChange={() => toggleLiveChart()}
                    checked={autoSyncEnabled}
                    disabled={disableLiveToggle}
                  />
                </span>
                <span className={cx(`${baseClass}-label`, checkedClass)}>
                  <Text size={"small"} colorModifier={disableLiveToggle ? "faintgrey" : null}>
                    {appstr("Automatically sync with source")}
                  </Text>
                </span>
              </span>
            )}
          </>
        )}
      </Flex>
      <Flex
        style={{ marginBottom: 0, marginTop: "auto" }}
        direction={"column"}
        gap={"4px"}
        padding={"16px 0 0 0"}
        className={"oc-left-justify"}
      >
        <Button
          dataTestId={`header-refresh-btn`}
          tooltipProps={{
            message: isCustomBackgroundDirty ? backgroundDirtyTooltip : lastRefreshed
          }}
          onClick={() => handleManualRefresh()}
          modifier={"secondary"}
          className={"oc-special-secondary"}
          disabled={disableManualRefresh}
          iconProps={{ name: "arrows-rotate" }}
          size={"small"}
        >
          {appstr("Refresh Now")}
        </Button>
      </Flex>
    </ToolbarPopover>
  );
};

export default SyncChartPopover;
